import { useEffect, useState } from 'react';
import { api } from '@/lib/api';

/**
 * What a NEW quote's terms should start from, for seeding the quote form.
 *
 * ## It returns `effectiveQuoteTerms`, never `quoteTerms`
 *
 * ⚠️ **The two fields on that response answer different questions, and the one
 * this hook does NOT return is the one the SETTINGS form binds to.** They differ
 * exactly when nothing is configured — the state every deployment starts in —
 * where the configured value is `null` and the effective one is the shipped
 * standard terms.
 *
 * Prefilling a quote from the configured value there would render an EMPTY terms
 * box while the server went on to write the standard terms into the document
 * anyway. **That is not a hypothetical; it is the bug this whole feature
 * exists to fix.** The quote form omits an empty text field rather than sending
 * an empty string, so the default has always been applied invisibly, and a
 * person who could not see it typed the wording out again.
 *
 * The settings card does the opposite, for the opposite reason: showing the
 * shipped constant in an editable box would promote a release default into
 * something that reads as saved policy, and an administrator could no longer
 * tell *"nothing is set"* from *"set to exactly the standard wording"*.
 *
 * **So the rule in one line: the quote form seeds from the RESOLVED default; the
 * settings form binds to the RAW stored value. Swapping them reproduces the
 * original bug.**
 *
 * ## Failure is silent, non-blocking, and costs visibility rather than correctness
 *
 * ⚠️ **A FAILED READ MUST NEVER STOP SOMEBODY RAISING A QUOTE.** Nothing here
 * raises a banner and nothing here throws: this is a convenience the person
 * never asked for, and a red block about a request they did not make is noise on
 * a form that works perfectly well without it.
 *
 * On failure `defaultTerms` stays `null`, so a caller seeds nothing, the box
 * stays empty, the form then omits `terms` on the create — and **the SERVER
 * applies the same default anyway**, because `createQuote` has always done so
 * when the key is absent. The document is correct either way; what is lost is
 * the person being able to SEE and edit the wording before they send it.
 *
 * `loading` is what makes that degradation honest rather than indistinguishable
 * from a slow network. `defaultTerms === null` alone cannot tell *"still
 * fetching"* from *"asked, and it failed"* — the effective value is never `null`
 * on a successful read, so `null` after `loading` has gone false means the read
 * did not land. A caller that wants to avoid seeding into a box the user has
 * already started typing in reads `loading`; a caller that just wants the value
 * can ignore it.
 *
 * @param enabled Read it as *this is a CREATE*. Pass `false` on an edit: seeding
 *   a stored document would rewrite terms somebody deliberately worded for a
 *   customer, and possibly already sent, because the ORGANISATION default has
 *   since changed. Disabled means no request is made at all.
 */
export function useQuoteTermsDefault(enabled = true): {
  /**
   * The resolved default — the wording a new quote should start from — or
   * `null` when the read is still in flight, has failed, or was never made.
   */
  defaultTerms: string | null;
  /**
   * `false` once the read has settled either way, and `false` immediately when
   * `enabled` is false, because nothing is being waited for.
   */
  loading: boolean;
} {
  const [defaultTerms, setDefaultTerms] = useState<string | null>(null);
  /*
   * Starts at `enabled` so the first paint of a create form reports that the
   * value is still coming, rather than reporting "asked, nothing there" about a
   * request that has not been made yet. Nothing calls setState synchronously in
   * the effect below, matching `useCycleOptions` and the other loaders here.
   */
  const [loading, setLoading] = useState(enabled);

  useEffect(() => {
    if (!enabled) {
      // An edit form waits for nothing, so it must not sit at `loading: true`
      // for ever — a caller gating a seed on it would then never run.
      setLoading(false);
      return;
    }
    setLoading(true);
    const controller = new AbortController();
    api
      .getQuoteTermsSettings(controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        /*
         * ⚠️ `effectiveQuoteTerms`, and it is the whole point of this file.
         * `quoteTerms` here would be `null` on every deployment where nobody has
         * configured anything, which is the commonest case and exactly the one
         * the seed exists for.
         */
        setDefaultTerms(envelope.data.effectiveQuoteTerms);
      })
      .catch(() => {
        /*
         * Deliberately silent, and deliberately not rethrown — see the header.
         * Left as `null` so no seed fires; the create then omits `terms` and the
         * server applies the same default itself.
         */
        if (controller.signal.aborted) return;
        setDefaultTerms(null);
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        setLoading(false);
      });
    return () => controller.abort();
  }, [enabled]);

  return { defaultTerms, loading };
}
